import { useCallback } from 'react';
import { hospitalizationsRequests } from 'redux/requests/hospitalizations.requests';
import { useApiCall } from 'redux/utils/apiHook.hook';
import type { NoteFieldSaveOverride } from '../NoteField';
import { useParentLinkedNoteSaveOverride } from './useParentLinkedNoteSaveOverride.hook';

/** Params for {@link useHospitalizationNoteSaveOverride}. */
export interface UseHospitalizationNoteSaveOverrideParams {
  hospitalizationId: number;
}

/**
 * Debounced hospitalization note autosave via parent PATCH (`updateHospitalization` with `{ note }`).
 *
 * @param params - Hospitalization id for the PATCH target.
 * @returns Save override for {@link NoteField}.
 */
export function useHospitalizationNoteSaveOverride({
  hospitalizationId,
}: UseHospitalizationNoteSaveOverrideParams): {
  hospitalizationNoteSaveOverride: NoteFieldSaveOverride | undefined;
} {
  const [
    _noteAutosaveResult,
    isNoteAutosaving,
    saveHospitalizationNoteOnly,
    noteAutosaveError,
  ] = useApiCall(hospitalizationsRequests.updateHospitalization);

  const saveHospitalizationNoteViaParent = useCallback(
    (text: string) => {
      saveHospitalizationNoteOnly({
        params: { id: hospitalizationId },
        body: { note: text },
      });
    },
    [hospitalizationId, saveHospitalizationNoteOnly]
  );

  const hospitalizationNoteSaveOverride = useParentLinkedNoteSaveOverride({
    enabled: true,
    onSaveNote: saveHospitalizationNoteViaParent,
    isSaving: isNoteAutosaving,
    error: noteAutosaveError,
  });

  return { hospitalizationNoteSaveOverride };
}
